import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, User, ShoppingBag, Package, LayoutGrid, ShoppingCart, Tag, Building2 } from 'lucide-react';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { usePortalCart } from '../../context/PortalCartContext';
import { Button } from '../../components/ui/button';
import PortalLangToggle from '../../components/portal/PortalLangToggle';
import { portalCatalogAPI } from '../../lib/portalApi';
import { BRAND_ASSETS } from '../../config/brandAssets';

const LOGO_SMALL = BRAND_ASSETS.LOGO_PRIMARY;

const NAV_DEFS = [
    { to: '/portal',         key: 'nav_home',     icon: Package,    end: true },
    { to: '/portal/catalog', key: 'nav_catalog',  icon: LayoutGrid },
    { to: '/portal/cart',    key: 'nav_cart',     icon: ShoppingCart },
    { to: '/portal/profile', key: 'nav_profile',  icon: User },
    { to: '/portal/orders',  key: 'nav_orders',   icon: ShoppingBag },
];

const SAFE_FONT = "'Century Gothic','CenturyGothic','Manrope','DM Sans',Arial,sans-serif";

/**
 * Read the active brand filter from `?brand=<id>` so the sidebar can
 * visually highlight whichever brand the catalog is currently filtered to.
 * Falls back to `null` for the "All brands" state.
 */
const useActiveBrandId = (pathname) => {
    const [brandId, setBrandId] = useState(null);
    useEffect(() => {
        if (!pathname.startsWith('/portal/catalog')) {
            setBrandId(null);
            return;
        }
        const sync = () => {
            const params = new URLSearchParams(window.location.search);
            setBrandId(params.get('brand') || null);
        };
        sync();
        window.addEventListener('popstate', sync);
        window.addEventListener('uc-portal-brand-change', sync);
        return () => {
            window.removeEventListener('popstate', sync);
            window.removeEventListener('uc-portal-brand-change', sync);
        };
    }, [pathname]);
    return brandId;
};

export const PortalLayout = ({ children }) => {
    const { t, i18n } = useTranslation('portal');
    const { email, logout, profile } = usePortalAuth();
    const { itemCount } = usePortalCart();
    const { pathname } = useLocation();
    const onCatalog = pathname.startsWith('/portal/catalog');
    const activeBrandId = useActiveBrandId(pathname);

    // Lazy-load brands once when the user lands on the catalog. Sidebar
    // stays empty on other routes so we never pay for an unused call.
    const [brands, setBrands] = useState([]);
    useEffect(() => {
        if (!onCatalog || brands.length > 0) return;
        portalCatalogAPI.brands()
            .then(({ data }) => setBrands(Array.isArray(data) ? data : []))
            .catch(() => { /* sidebar brand list is progressive enhancement */ });
    }, [onCatalog, brands.length]);

    // Sync customer's saved `preferred_locale` with i18next on profile load.
    useEffect(() => {
        const stored = window.localStorage.getItem('uc.lang');
        const pref = profile?.preferred_locale;
        if (pref && !stored && pref !== i18n.resolvedLanguage) {
            i18n.changeLanguage(pref);
        }
    }, [profile?.preferred_locale, i18n]);

    // Identity shown in the header. Prefer the company name (B2B context)
    // and fall back to the email so freshly-signed-up buyers still see
    // something useful before completing their profile.
    const identityLabel = (profile?.company_name || '').trim() || email;
    const identityIsCompany = identityLabel !== email;

    return (
        <div className="min-h-screen bg-[#FBF8F6] overflow-x-hidden" style={{ fontFamily: SAFE_FONT }} data-testid="portal-layout">
            {/* Top bar */}
            <header className="bg-white border-b border-[#EFE5E1] sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
                    <Link to="/portal" className="flex items-center gap-3 min-w-0" data-testid="portal-home-link">
                        <img src={LOGO_SMALL} alt="UC" className="w-9 h-9 rounded-full object-cover shrink-0" />
                        <div className="leading-tight hidden sm:block">
                            <div className="text-[15px] font-semibold tracking-[2px]">UNIVERS COSMETIC</div>
                            <div className="text-[10px] uppercase tracking-[3px] text-[#C08478]">{t('login.hero_subtitle')}</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <PortalLangToggle />
                        <Link
                            to="/portal/cart"
                            data-testid="portal-cart-icon"
                            aria-label={t('layout.cart_aria')}
                            className="relative w-10 h-10 rounded-full bg-[#FBF8F6] hover:bg-[#F5EEEA] flex items-center justify-center text-[#1A1A1A] transition-colors"
                        >
                            <ShoppingCart size={18} />
                            {itemCount > 0 && (
                                <span
                                    data-testid="portal-cart-badge"
                                    className="absolute -top-1 -right-1 bg-[#C08478] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                                >
                                    {itemCount > 99 ? '99+' : itemCount}
                                </span>
                            )}
                        </Link>
                        <span
                            className="hidden md:inline-flex items-center gap-1.5 text-sm text-gray-600 max-w-[200px] truncate"
                            title={identityIsCompany ? `${identityLabel} · ${email}` : email}
                            data-testid={identityIsCompany ? 'portal-company-display' : 'portal-email-display'}
                        >
                            {identityIsCompany && <Building2 size={13} className="text-[#C08478] shrink-0" />}
                            <span className="truncate">{identityLabel}</span>
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => { await logout(); window.location.href = '/portal/login'; }}
                            className="text-gray-600 hover:text-[#1A1A1A]"
                            data-testid="portal-logout-btn"
                        >
                            <LogOut size={16} className="sm:mr-1.5" />
                            <span className="hidden sm:inline">{t('layout.sign_out')}</span>
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6 lg:gap-8">
                {/* Side nav */}
                <aside className="self-start min-w-0">
                    <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-2 px-2 lg:mx-0 lg:px-0">
                        {NAV_DEFS.map((item) => {
                            const isActive = item.end
                                ? pathname === item.to
                                : pathname.startsWith(item.to);
                            const Icon = item.icon;
                            const label = t(`layout.${item.key}`);
                            const cls = `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors whitespace-nowrap ${
                                item.disabled
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : isActive
                                        ? 'bg-[#1A1A1A] text-white'
                                        : 'text-gray-600 hover:bg-[#F5EEEA] hover:text-[#1A1A1A]'
                            }`;
                            if (item.disabled) {
                                return (
                                    <span key={item.to} className={cls}>
                                        <Icon size={16} /> {label}
                                        <span className="ml-auto text-[9px] uppercase tracking-wider">{t('layout.soon')}</span>
                                    </span>
                                );
                            }
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className={cls}
                                    data-testid={`portal-nav-${item.key}`}
                                >
                                    <Icon size={16} /> {label}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Contextual brand list — only on catalog */}
                    {onCatalog && brands.length > 0 && (
                        <div className="mt-6 hidden lg:block animate-fadeIn" data-testid="sidebar-brands">
                            <p className="text-[10px] uppercase tracking-[3px] text-[#C08478] font-semibold mb-2 px-3 flex items-center gap-1.5">
                                <Tag size={11} /> {t('catalog.sidebar_brands')}
                            </p>
                            <div className="flex flex-col gap-0.5" data-testid="sidebar-brand-list">
                                <Link
                                    to="/portal/catalog"
                                    onClick={() => window.dispatchEvent(new Event('uc-portal-brand-change'))}
                                    data-testid="sidebar-brand-all"
                                    className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                                        !activeBrandId
                                            ? 'bg-[#F5EEEA] text-[#1A1A1A] font-semibold'
                                            : 'text-gray-500 hover:bg-[#FBF8F6] hover:text-[#1A1A1A]'
                                    }`}
                                >
                                    <span className="truncate">{t('catalog.all_brands')}</span>
                                    <span className="text-[10px] text-gray-400 tabular-nums">
                                        {brands.reduce((s, b) => s + (b.product_count || 0), 0)}
                                    </span>
                                </Link>
                                {brands.map((b) => (
                                    <Link
                                        key={b.id}
                                        to={`/portal/catalog?brand=${encodeURIComponent(b.id)}`}
                                        onClick={() => window.dispatchEvent(new Event('uc-portal-brand-change'))}
                                        data-testid={`sidebar-brand-${b.id}`}
                                        className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                                            activeBrandId === b.id
                                                ? 'bg-[#1A1A1A] text-white font-semibold'
                                                : 'text-gray-500 hover:bg-[#FBF8F6] hover:text-[#1A1A1A]'
                                        }`}
                                    >
                                        <span className="truncate">{b.name}</span>
                                        <span className="text-[10px] opacity-70 tabular-nums">{b.product_count}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>

                {/* Main content — `min-w-0` prevents children from blowing past the column */}
                <main className="min-w-0">{children}</main>
            </div>
        </div>
    );
};

export default PortalLayout;
